import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import MapView from "@/components/MapView";
import { Loading, ErrorState } from "@/components/common/States";
import StatusBadge from "@/components/common/StatusBadge";
import { fmtMins, fmtTimeIST } from "@/lib/dateRange";
import { Users } from "lucide-react";

const stateOf = (e) => (e.status === "working" ? (e.stale ? "stale" : "working") : e.status);
const COLORS = { working: "#059669", stale: "#D97706", ended: "#64748B" };
const LABEL = { working: "Working", stale: "Location stale", ended: "Ended", not_started: "Not started" };
const BADGE = { working: "working", stale: "stale", ended: "ended", not_started: "New" };

function Tile({ value, label, tone, testid }) {
  return (
    <div className={`rounded-xl border p-4 ${tone}`} data-testid={testid}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs font-medium">{label}</div>
    </div>
  );
}

function EmployeeRow({ e, onOpen }) {
  const st = stateOf(e);
  return (
    <button onClick={() => onOpen(e.user_id)} data-testid={`employee-${e.user_id}`}
      className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-1 border-b border-slate-100 py-2.5 last:border-0 text-sm hover:bg-slate-50 rounded px-2 text-left transition-colors">
      <span className="font-medium text-slate-700">{e.user_name}</span>
      <span className="flex flex-wrap items-center gap-3 text-slate-500">
        {e.status === "working" && e.minutes_since_update != null && (
          <span className={e.stale ? "text-amber-600 font-medium" : ""} data-testid={`employee-last-update-${e.user_id}`}>
            {e.stale ? `No update for ${e.minutes_since_update} min` : `Updated ${e.minutes_since_update} min ago`}
          </span>
        )}
        {e.start_at && <span>From {fmtTimeIST(e.start_at)}</span>}
        {e.distance_km != null && <span>{(e.distance_km || 0).toFixed(1)} km</span>}
        {e.duration_min != null && <span>{fmtMins(e.duration_min)}</span>}
        <StatusBadge status={BADGE[st]} label={LABEL[st]} testid={`employee-status-${e.user_id}`} />
      </span>
    </button>
  );
}

export default function TeamOverview({ date, onOpen }) {
  const { apiParams } = useFilters("today");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/work/map", { params: { date, ...(apiParams.salesperson ? { salesperson: apiParams.salesperson } : {}) } });
      setData(data); setError("");
    } catch (e) { setError(apiError(e)); }
  }, [date, apiParams.salesperson]);
  useEffect(() => { setData(null); load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Loading field map…" />;

  const active = [...data.working, ...data.ended];
  const markers = active.filter((e) => e.lat != null).map((e) => {
    const st = stateOf(e);
    return { lat: e.lat, lng: e.lng, color: COLORS[st], label: `${e.user_name} · ${LABEL[st]}` };
  });
  const everyone = [...data.working, ...data.ended, ...data.not_started.map((e) => ({ ...e, status: "not_started" }))];

  return (
    <div className="space-y-6" data-testid="team-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile testid="stat-working" value={data.working.length - data.stale_count} label="Working (live)" tone="border-emerald-200 bg-emerald-50 text-emerald-700" />
        <Tile testid="stat-stale" value={data.stale_count} label={`Stale > ${data.stale_minutes} min`} tone="border-amber-300 bg-amber-50 text-amber-700" />
        <Tile testid="stat-ended" value={data.ended.length} label="Ended" tone="border-slate-200 bg-slate-50 text-slate-700" />
        <Tile testid="stat-notstarted" value={data.not_started.length} label="Not started" tone="border-sky-200 bg-sky-50 text-sky-700" />
      </div>
      <MapView markers={markers} height={420} />
      <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-5">
        <h3 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2"><Users className="h-5 w-5 text-teal" /> Employees</h3>
        <p className="text-sm text-slate-500 mb-3">Tap an employee to see their full shift route, stops and timeline.</p>
        {everyone.length === 0 ? <p className="text-sm text-slate-400" data-testid="no-employees">No field employees in your scope yet.</p> :
          everyone.map((e) => <EmployeeRow key={e.user_id} e={e} onOpen={onOpen} />)}
      </div>
    </div>
  );
}
